import { Link } from 'react-router-dom';
import { Music2, ListMusic } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Navigation } from '@/components/Navigation';
import { PlayerBar } from '@/components/PlayerBar';
import { TrackCard } from '@/components/TrackCard';
import { PlaylistCard } from '@/components/PlaylistCard';
import { ZapButton } from '@/components/ZapButton';
import { EditProfileDialog } from '@/components/EditProfileDialog';
import { useAuthor } from '@/hooks/useAuthor';
import { useMusicTracksByAuthor } from '@/hooks/useMusicTracksByAuthor';
import { usePlaylists } from '@/hooks/usePlaylists';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { genUserName } from '@/lib/genUserName';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';

interface ProfilePageProps {
  pubkey: string;
}

export function ProfilePage({ pubkey }: ProfilePageProps) {
  const queryClient = useQueryClient();
  const author = useAuthor(pubkey);
  const { data: tracks, isLoading: isLoadingTracks } = useMusicTracksByAuthor(pubkey);
  const { data: playlists, isLoading: isLoadingPlaylists, refetch: refetchPlaylists } = usePlaylists({ pubkey });
  const { user } = useCurrentUser();
  const isOwnProfile = user?.pubkey === pubkey;

  const handleTrackCreate = () => {
    // Invalidate tracks query to refetch the updated list
    queryClient.invalidateQueries({ queryKey: ['music-tracks-by-author', pubkey] });
  };

  const metadata = author.data?.metadata;
  const displayName = metadata?.name || genUserName(pubkey);
  const avatarUrl = metadata?.picture;
  const about = metadata?.about;

  // Calculate total zaps across all tracks and playlists
  const trackATags = tracks?.map((t) => `${t.event.kind}:${t.pubkey}:${t.id}`) || [];
  const playlistATags = playlists?.map((p) => `${p.event.kind}:${p.pubkey}:${p.id}`) || [];
  const allATags = [...trackATags, ...playlistATags];

  const { data: totalZaps, isLoading: isLoadingZaps } = useTotalZaps(allATags);

  const zapCount = totalZaps?.count || 0;
  const zapAmount = totalZaps?.amount || 0;

  if (author.isLoading) {
    return <ProfilePageSkeleton />;
  }

  // Use profile picture for background if available
  const backgroundStyle = avatarUrl
    ? { backgroundImage: `url(${avatarUrl})` }
    : { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Global Navigation */}
      <Navigation
        onPlaylistCreate={() => refetchPlaylists()}
        onTrackCreate={handleTrackCreate}
      />

      {/* Hero Section with blurred background */}
      <div className="relative">
        {/* Blurred background */}
        <div
          className="absolute top-0 left-0 right-0 h-[250px] md:h-[300px] bg-cover bg-center blur-3xl opacity-45"
          style={backgroundStyle}
        />

        {/* Content */}
        <div className="relative mx-auto px-4 py-8 max-w-4xl 2xl:max-w-5xl">
          <div className="grid md:grid-cols-[300px,1fr] gap-8 max-w-lg md:max-w-none mx-auto">
            {/* Profile Image */}
            <div
              className="w-full aspect-square rounded-lg bg-cover bg-center shadow-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]"
              style={backgroundStyle}
            >
              {!avatarUrl && (
                <div className="h-full flex items-center justify-center bg-black/20 rounded-lg text-6xl font-bold text-white/80">
                  {displayName.slice(0, 2).toUpperCase()}
                </div>
              )}
            </div>

            {/* Right Column: Profile Info + Tracks */}
            <div className="space-y-8">
              {/* Profile Info */}
              <div className="space-y-6">
                {/* Name and Description */}
                <div>
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h1 className="text-2xl md:text-4xl font-bold">{displayName}</h1>
                    {isOwnProfile && <EditProfileDialog />}
                  </div>
                  {about && (
                    <p className="text-muted-foreground whitespace-pre-wrap break-words">
                      {about}
                    </p>
                  )}
                </div>

                {/* Zap Stats */}
                <div className="flex items-center gap-4">
                  <ZapButton
                    recipientPubkey={pubkey}
                    size="lg"
                    variant="ghost"
                    className="h-14 w-14 rounded-full bg-white/[0.07] hover:bg-white/[0.12]"
                  />
                  <div>
                    {isLoadingZaps ? (
                      <>
                        <Skeleton className="h-8 w-32 mb-1" />
                        <Skeleton className="h-4 w-24" />
                      </>
                    ) : (
                      <>
                        <p className="text-2xl font-bold">
                          {formatSats(zapAmount)} sats
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {zapCount} {zapCount === 1 ? 'zap' : 'zaps'}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Tracks and Playlists Tabs */}
              <Tabs defaultValue="tracks" className="space-y-4">
                <TabsList className="h-auto glass border-0 p-1 rounded-full mb-6">
                  <TabsTrigger value="tracks" className="text-xs px-3 py-1.5 h-7 rounded-full data-[state=active]:glass-strong">
                    <Music2 className="h-4 w-4 mr-2" />
                    Tracks
                    {!isLoadingTracks && tracks && (
                      <Badge variant="secondary" className="ml-2 h-4 px-1.5 text-[10px] leading-none bg-white/10 hover:bg-white/10">
                        {tracks.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="playlists" className="text-xs px-3 py-1.5 h-7 rounded-full data-[state=active]:glass-strong">
                    <ListMusic className="h-4 w-4 mr-2" />
                    Playlists
                    {!isLoadingPlaylists && playlists && (
                      <Badge variant="secondary" className="ml-2 h-4 px-1.5 text-[10px] leading-none bg-white/10 hover:bg-white/10">
                        {playlists.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="tracks" className="space-y-4">
                  {isLoadingTracks ? (
                    <div className="space-y-4">
                      {[...Array(3)].map((_, i) => (
                        <TrackCardSkeleton key={i} />
                      ))}
                    </div>
                  ) : tracks && tracks.length > 0 ? (
                    <div className="space-y-4">
                      {tracks.map((track) => (
                        <TrackCard key={`${track.pubkey}:${track.id}`} track={track} playlist={tracks} />
                      ))}
                    </div>
                  ) : (
                    <Card className="border-dashed">
                      <CardContent className="py-12 px-8 text-center">
                        <Music2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">No tracks yet</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="playlists" className="space-y-4">
                  {isLoadingPlaylists ? (
                    <div className="space-y-4">
                      {[...Array(3)].map((_, i) => (
                        <TrackCardSkeleton key={i} />
                      ))}
                    </div>
                  ) : playlists && playlists.length > 0 ? (
                    <div className="space-y-4">
                      {playlists.map((playlist) => (
                        <PlaylistCard key={playlist.id} playlist={playlist} onUpdate={() => refetchPlaylists()} />
                      ))}
                    </div>
                  ) : (
                    <Card className="border-dashed">
                      <CardContent className="py-12 px-8 text-center">
                        <ListMusic className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">No playlists yet</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Player Bar */}
      <PlayerBar />
    </div>
  );
}



function TrackCardSkeleton() {
  return (
    <Card className="glass border-0 overflow-hidden">
      <div className="flex items-center gap-4 p-2">
        <Skeleton className="w-16 h-16 rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-2 min-w-0">
          <Skeleton className="h-5 w-3/4" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4 rounded-full" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2">
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-10 w-10 rounded-full" />
        </div>
      </div>
    </Card>
  );
}

function ProfilePageSkeleton() {
  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/[0.11] backdrop-blur-md">
        <div className="w-full px-4 py-2 md:px-6 md:py-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-8 w-24" />
            <div className="flex items-center gap-3">
              <Skeleton className="h-9 w-32" />
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="relative">
        <div className="relative mx-auto px-4 py-8 max-w-4xl 2xl:max-w-5xl">
          <div className="grid md:grid-cols-[300px,1fr] gap-8 max-w-lg md:max-w-none mx-auto">
            <Skeleton className="w-full aspect-square rounded-lg" />
            <div className="space-y-8">
              {/* Profile Info */}
              <div className="space-y-6">
                <div>
                  <Skeleton className="h-10 w-48 mb-2" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4 mt-1" />
                </div>
                <div className="flex items-center gap-4">
                  <Skeleton className="h-14 w-14 rounded-full" />
                  <div className="space-y-1">
                    <Skeleton className="h-8 w-32" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="space-y-4">
                <Skeleton className="h-8 w-48 rounded-full" />
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <TrackCardSkeleton key={i} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Hook to fetch zaps for multiple addressable events (tracks and playlists)
function useTotalZaps(aTags: string[]) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['total-zaps', aTags],
    queryFn: async (c) => {
      if (aTags.length === 0) {
        return { amount: 0, count: 0 };
      }

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);

      // Query zap receipts for addressable events using 'a' tags
      const zapReceipts = await nostr.query(
        [{ kinds: [9735], '#a': aTags, limit: 1000 }],
        { signal }
      );

      let totalAmount = 0;
      let totalCount = 0;

      for (const receipt of zapReceipts) {
        const bolt11Tag = receipt.tags.find(([name]) => name === 'bolt11');
        if (bolt11Tag && bolt11Tag[1]) {
          const amount = parseBolt11Amount(bolt11Tag[1]);
          if (amount > 0) {
            totalAmount += amount;
            totalCount++;
          }
        }
      }

      return { amount: totalAmount, count: totalCount };
    },
    enabled: aTags.length > 0,
  });
}

/**
 * Simple bolt11 amount parser
 * Extracts the amount in millisats from a bolt11 invoice
 * Format: ln[bc|tb|bcrt][amount][multiplier]1[data]
 */
function parseBolt11Amount(bolt11: string): number {
  try {
    // Lightning invoices format: lnbc[amount][multiplier]1...
    // The "1" separates the human-readable part from the data part
    const match = bolt11.match(/ln(?:bc|tb|bcrt)([0-9]+)([munp]?)1/);
    if (!match) return 0;

    const amount = parseInt(match[1]);
    if (isNaN(amount) || amount <= 0) return 0;

    const multiplier = match[2];

    // Convert to millisats based on multiplier
    let millisats = 0;
    switch (multiplier) {
      case 'm': // milli-bitcoin (0.001 BTC)
        millisats = amount * 100_000_000; // 0.001 BTC = 100,000 sats = 100,000,000 millisats
        break;
      case 'u': // micro-bitcoin (0.000001 BTC)
        millisats = amount * 100_000; // 0.000001 BTC = 100 sats = 100,000 millisats
        break;
      case 'n': // nano-bitcoin (0.000000001 BTC)
        millisats = amount * 100; // 0.000000001 BTC = 0.1 sats = 100 millisats
        break;
      case 'p': // pico-bitcoin (0.000000000001 BTC)
        millisats = amount / 10; // 0.000000000001 BTC = 0.0001 sats = 0.1 millisats
        break;
      default:
        // No multiplier means the amount is in bitcoin
        millisats = amount * 100_000_000_000; // 1 BTC = 100,000,000 sats = 100,000,000,000 millisats
    }

    return Math.floor(millisats);
  } catch {
    return 0;
  }
}



/**
 * Format millisats to a readable string
 */
function formatSats(millisats: number): string {
  const sats = Math.floor(millisats / 1000);
  if (sats >= 1_000_000) {
    return `${(sats / 1_000_000).toFixed(1)}M`;
  }
  if (sats >= 1_000) {
    return `${(sats / 1_000).toFixed(1)}K`;
  }
  return sats.toString();
}
